import React from "react";
import { toAppUrl } from "../../../assets/js/utils/functions";
import useCopyClipboard from "../../hooks/useCopyClipboard";
import Modal, { ModalBody } from "../../ui/modal";

interface Props {
  show: boolean;
  toggle: (state: boolean) => void;
  slug: string;
}

enum SHARE_METHODS {
  WHATSAPP = "WHATSAPP",
}

const ShareHighlightModal: React.FC<Props> = ({ show, toggle, slug }) => {
  const link = toAppUrl(`products/${slug}`);
  const [isCopied, copy] = useCopyClipboard(link, { successDuration: 1000 });

  const share = async (method: SHARE_METHODS) => {
    const message = `Hi, here's a link to your prouct: ${link}`;

    switch (method) {
      case SHARE_METHODS.WHATSAPP:
        window.open(`https://api.whatsapp.com/send?text=${message}`, "_blank");
        break;
      default:
        const shareData = {
          title: "Product Link",
          text: "Hi, here's a link to your product",
          url: link,
        };

        if (navigator?.share) {
          await navigator.share(shareData);
        } else {
          alert("Cannot share this product");
        }

        break;
    }
  };

  return (
    <Modal title="Share" {...{ show, toggle }} size="midi">
      <ModalBody>
        <div className="flex flex-col items-center w-full">

          <div className="w-full mt-0">
            <div className="flex items-center mt-1 py-3 relative overflow-hidden">
               <div className="flex gap-2 items-center">
                    <button
                        className="h-8 w-8 sm:h-10 sm:w-10 rounded-full bg-grey-fields-100 flex items-center justify-center text-[#DB3E56]"
                    >
                        {/* prettier-ignore */}
                        <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                        <path d="M9.79489 8.20508C11.4824 9.89258 11.4824 12.6226 9.79489 14.3026C8.10739 15.9826 5.37738 15.9901 3.69738 14.3026C2.01738 12.6151 2.00988 9.88508 3.69738 8.20508" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M7.94223 10.0577C6.18723 8.30266 6.18723 5.45266 7.94223 3.69016C9.69723 1.92766 12.5472 1.93516 14.3097 3.69016C16.0722 5.44516 16.0647 8.29516 14.3097 10.0577" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                    </button>
                    <span className="font-semibold font-display text-sm sm:text-1sm text-black">
                        Copy Link
                    </span>
                </div>
              <button
                className="bg-white text-1xs text-black font-semibold flex items-center rounded-30 p-2.5 absolute right-3.5 shadow-pill"
                onClick={() => copy()}
              >
                {/* prettier-ignore */}
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 9.675V12.825C12 15.45 10.95 16.5 8.325 16.5H5.175C2.55 16.5 1.5 15.45 1.5 12.825V9.675C1.5 7.05 2.55 6 5.175 6H8.325C10.95 6 12 7.05 12 9.675Z" stroke="#767676" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/>
                    <path d="M16.5 5.175V8.325C16.5 10.95 15.45 12 12.825 12H12V9.675C12 7.05 10.95 6 8.325 6H6V5.175C6 2.55 7.05 1.5 9.675 1.5H12.825C15.45 1.5 16.5 2.55 16.5 5.175Z" stroke="#767676" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </button>
            </div>

            <div className="flex mt-1 mb-4 border-t border-grey-divider py-4 items-center mt-1 relative overflow-hidden">
               <div className="flex gap-2 items-center cursor-pointer"  onClick={() => share(SHARE_METHODS.WHATSAPP)}>
                    <button
                        className="h-8 w-8 sm:h-10 sm:w-10 rounded-full bg-green-500 bg-opacity-10 text-green-500 flex items-center justify-center "
                    >
                        {/* prettier-ignore */}
                        <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                            <path d="M16.4847 8.55734C16.2297 4.20734 12.2772 0.854849 7.72473 1.60485C4.58973 2.12235 2.07723 4.66483 1.58973 7.79983C1.30473 9.61483 1.67975 11.3323 2.49725 12.7498L1.82974 15.2323C1.67974 15.7948 2.19722 16.3048 2.75222 16.1473L5.19723 15.4723C6.30723 16.1248 7.60473 16.4998 8.99223 16.4998C13.2222 16.4998 16.7322 12.7723 16.4847 8.55734ZM12.6597 11.7898C12.5922 11.9248 12.5097 12.0523 12.4047 12.1723C12.2172 12.3748 12.0147 12.5248 11.7897 12.6148C11.5647 12.7123 11.3172 12.7573 11.0547 12.7573C10.6722 12.7573 10.2597 12.6673 9.83222 12.4798C9.39722 12.2923 8.96975 12.0448 8.54225 11.7373C8.10725 11.4223 7.70223 11.0698 7.31223 10.6873C6.92224 10.2973 6.57722 9.88483 6.26222 9.45733C5.95472 9.02983 5.70723 8.60233 5.52723 8.17483C5.34723 7.74733 5.25724 7.33485 5.25724 6.94485C5.25724 6.68985 5.30224 6.44234 5.39224 6.21734C5.48224 5.98484 5.62475 5.77485 5.82725 5.58735C6.06725 5.34735 6.32973 5.23485 6.60723 5.23485C6.71223 5.23485 6.81721 5.25734 6.91471 5.30234C7.01221 5.34734 7.10223 5.41484 7.16973 5.51234L8.03971 6.74233C8.10721 6.83983 8.15972 6.92234 8.18972 7.00484C8.22722 7.08734 8.24223 7.16233 8.24223 7.23733C8.24223 7.32733 8.21224 7.41735 8.15974 7.50735C8.10724 7.59735 8.03972 7.68734 7.94972 7.77734L7.66471 8.07733C7.61971 8.12233 7.60475 8.16734 7.60475 8.22734C7.60475 8.25734 7.61221 8.28734 7.61971 8.31734C7.63471 8.34734 7.64224 8.36984 7.64974 8.39234C7.71724 8.51984 7.83722 8.67733 8.00222 8.87233C8.17472 9.06733 8.35475 9.26983 8.54975 9.46484C8.75225 9.66733 8.94724 9.84733 9.14974 10.0198C9.34474 10.1848 9.50972 10.2973 9.63722 10.3648C9.65972 10.3723 9.68224 10.3873 9.70474 10.3948C9.73474 10.4098 9.76474 10.4098 9.80224 10.4098C9.86974 10.4098 9.91476 10.3873 9.95976 10.3423L10.2447 10.0573C10.3422 9.95984 10.4322 9.89234 10.5147 9.84734C10.6047 9.79484 10.6872 9.76483 10.7847 9.76483C10.8597 9.76483 10.9347 9.77984 11.0172 9.81734C11.0997 9.85484 11.1897 9.89985 11.2797 9.96735L12.5247 10.8523C12.6222 10.9198 12.6897 11.0023 12.7347 11.0923C12.7722 11.1898 12.7947 11.2798 12.7947 11.3848C12.7497 11.5123 12.7197 11.6548 12.6597 11.7898Z" fill="currentColor"/>
                        </svg>
                    </button>
                    <span className="font-semibold font-display text-sm sm:text-1sm text-black">
                    Share to Whatsapp
                    </span>
                </div>
              
            </div>
          
          </div>
        </div>
      </ModalBody>
    </Modal>
  );
};

export default ShareHighlightModal;
